import { useId, useRef, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { SERVICE_TYPES, SERVICE_TYPE_LABELS, fulfillmentTypeFor } from "@/types/quotes";
import { useCreateQuote } from "@/hooks/useQuotes";
import { useFindOrCreateContact, type Contact } from "@/hooks/useContacts";
import type { QuoteTemplateRow } from "@/hooks/useQuoteTemplates";
import { templateToFormPrefill } from "@/lib/quoteTemplates";
import { lineTotal } from "@/lib/quote-totals";
import { ContactSelector } from "./ContactSelector";

const DEFAULT_GUEST_COUNT = 50;
// The quotes.event_date check constraint's range (#696).
const MIN_DATE = "2020-01-01";
const MAX_DATE = "2031-12-31";
// Ontario HST, as the drawer's quotes store it.
const TAX_RATE = 13;

const selectCls = "flex h-9 w-full rounded-md border border-input bg-background px-3 text-sm text-foreground";

interface NewQuoteDialogProps {
  open: boolean;
  onClose: () => void;
  /** After the draft is created, with its id; the caller opens the drawer on it. */
  onCreated: (quoteId: string) => void;
  /** Start from this template (Templates tab → Use template). */
  initialTemplate?: QuoteTemplateRow | null;
}

/**
 * New quote (#1559): the few facts a draft needs. It creates the draft and
 * hands its id to onCreated, and the quote drawer takes it from there. Event
 * type, time and address are set in the drawer's Edit details.
 */
export function NewQuoteDialog({ open, onClose, onCreated, initialTemplate }: NewQuoteDialogProps) {
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display text-xl">New quote</DialogTitle>
        </DialogHeader>
        {/* Mounted only while open, so each open starts blank (#728). */}
        {open && <NewQuoteForm template={initialTemplate ?? null} onCancel={onClose} onCreated={onCreated} />}
      </DialogContent>
    </Dialog>
  );
}

interface NewQuoteFormProps {
  template: QuoteTemplateRow | null;
  onCancel: () => void;
  onCreated: (quoteId: string) => void;
}

function NewQuoteForm({ template, onCancel, onCreated }: NewQuoteFormProps) {
  const createQuote = useCreateQuote();
  const findOrCreate = useFindOrCreateContact();
  const fieldId = useId();
  const idFor = (name: string) => `${fieldId}-${name}`;

  // The template's lines, service type and guests start the form (#810).
  const [prefill] = useState(() => (template ? templateToFormPrefill(template, DEFAULT_GUEST_COUNT, "start") : null));
  const [clientName, setClientName] = useState("");
  const [clientEmail, setClientEmail] = useState("");
  const [clientPhone, setClientPhone] = useState("");
  const [contactId, setContactId] = useState<string | undefined>(undefined);
  const [eventDate, setEventDate] = useState("");
  const [serviceType, setServiceType] = useState(prefill?.serviceType ?? "drop_off");
  const [guestCount, setGuestCount] = useState(prefill?.guestCount ?? DEFAULT_GUEST_COUNT);
  const [dateError, setDateError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  // A second submit while the first is still creating would make a second draft.
  const inFlight = useRef(false);

  const handleSelectContact = (contact: Contact) => {
    setContactId(contact.id);
    setClientName(`${contact.first_name} ${contact.last_name || ""}`.trim());
    setClientEmail(contact.email ?? "");
    setClientPhone(contact.phone ?? "");
  };

  const handleClearContact = () => {
    setContactId(undefined);
    setClientName("");
    setClientEmail("");
    setClientPhone("");
  };

  const handleManualChange = (field: "name" | "email" | "phone", value: string) => {
    if (field === "name") setClientName(value);
    else if (field === "email") setClientEmail(value);
    else setClientPhone(value);
    // Typing by hand unlinks the picked contact.
    setContactId(undefined);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (inFlight.current || !clientName.trim() || !eventDate) return;
    // ISO dates compare as strings.
    if (eventDate < MIN_DATE || eventDate > MAX_DATE) {
      setDateError("Event date must be between January 1, 2020 and December 31, 2031.");
      return;
    }
    setDateError(null);
    inFlight.current = true;
    setSubmitting(true);
    try {
      // Link or make the contact from a typed email. Non-blocking: the quote
      // still saves without a contact link.
      let resolvedContactId = contactId;
      if (!resolvedContactId && clientEmail.trim()) {
        try {
          const nameParts = clientName.trim().split(/\s+/);
          const contact = await findOrCreate.mutateAsync({
            first_name: nameParts[0] || clientName.trim(),
            last_name: nameParts.slice(1).join(" ") || undefined,
            email: clientEmail.trim(),
            phone: clientPhone.trim() || undefined,
          });
          resolvedContactId = contact?.id;
        } catch {
          // Saved without a contact link.
        }
      }

      const lineItems = (prefill?.lineItems ?? []).map((li) => {
        const quantity = li.pricing_type === "per_person" ? guestCount : li.quantity;
        return {
          menu_item_id: li.menu_item_id ?? null,
          package_id: li.package_id ?? null,
          name: li.name,
          description: li.description,
          pricing_type: li.pricing_type,
          unit_price: li.unit_price,
          quantity,
          line_total: lineTotal(li.unit_price, quantity),
          sort_order: 0,
        };
      });

      // Blank optional fields stay undefined, so the insert leaves them out.
      const created = await createQuote.mutateAsync({
        client_name: clientName.trim(),
        client_email: clientEmail.trim() || undefined,
        client_phone: clientPhone.trim() || undefined,
        contact_id: resolvedContactId || undefined,
        event_type_id: prefill?.eventTypeId || undefined,
        event_date: eventDate,
        service_type: serviceType,
        // Kept in step with service_type, the source of truth (#873).
        fulfillment_type: fulfillmentTypeFor(serviceType),
        guest_count: guestCount,
        notes: prefill?.notes || undefined,
        payment_due_date: eventDate,
        tax_rate: TAX_RATE,
        line_items: lineItems,
      });
      onCreated(created.id);
    } catch {
      // useCreateQuote's onError has shown the toast; the form stays open to retry.
    } finally {
      inFlight.current = false;
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {template && <p className="text-sm text-muted-foreground">From template "{template.name}"</p>}
      <ContactSelector
        clientName={clientName}
        clientEmail={clientEmail}
        clientPhone={clientPhone}
        contactId={contactId}
        onSelect={handleSelectContact}
        onClear={handleClearContact}
        onManualChange={handleManualChange}
      />
      <div className="grid grid-cols-2 gap-4 max-[480px]:grid-cols-1">
        <div className="space-y-1.5 col-span-full">
          <Label htmlFor={idFor("event-date")} className="text-xs font-medium">Event date *</Label>
          <Input
            id={idFor("event-date")}
            type="date"
            min={MIN_DATE}
            max={MAX_DATE}
            required
            value={eventDate}
            aria-describedby={dateError ? idFor("event-date-error") : undefined}
            onChange={(e) => {
              setEventDate(e.target.value);
              setDateError(null);
            }}
          />
          {dateError && (
            <p id={idFor("event-date-error")} role="alert" className="text-xs text-destructive">{dateError}</p>
          )}
        </div>
        <div className="space-y-1.5">
          <Label htmlFor={idFor("service-type")} className="text-xs font-medium">Service type</Label>
          <select id={idFor("service-type")} value={serviceType} onChange={(e) => setServiceType(e.target.value)} className={selectCls}>
            {SERVICE_TYPES.map((st) => <option key={st} value={st}>{SERVICE_TYPE_LABELS[st]}</option>)}
          </select>
        </div>
        <div className="space-y-1.5">
          <Label htmlFor={idFor("guests")} className="text-xs font-medium">Guests</Label>
          <Input
            id={idFor("guests")}
            type="number"
            min="1"
            value={guestCount}
            onChange={(e) => setGuestCount(parseInt(e.target.value, 10) || 1)}
          />
        </div>
      </div>
      <div className="flex justify-end gap-2 pt-2">
        <Button type="button" variant="outline" onClick={onCancel}>Cancel</Button>
        <Button type="submit" disabled={submitting || createQuote.isPending}>
          {submitting ? "Creating…" : "Create quote"}
        </Button>
      </div>
    </form>
  );
}
